import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { useState, useMemo } from "react";
import { signIn } from "../../utils/kintopiaApi";

type Props = Readonly<{
  open: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}>;

export default function SignInDialog({ open, onClose, onLoginSuccess }: Props) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  /**
   * Email validation without regex backtracking.
   * - Linear time, deterministic.
   * - Anchors via simple index checks, no nested quantifiers.
   * - Length cap protects against oversized payloads.
   *
   * Reviewed for Sonar rule S5852 (slow regular expressions).
   */
  const MAX_EMAIL_LEN = 320; // RFC-informed upper bound (64 local + 255 domain, plus separators)

  function isLikelyEmailNoRegex(s: string): boolean {
    if (typeof s !== "string") return false;
    // basic shape and size
    if (s.length < 3 || s.length > MAX_EMAIL_LEN) return false;

    const at = s.indexOf("@");
    if (at <= 0 || at !== s.lastIndexOf("@")) return false;

    const local = s.slice(0, at);
    const domain = s.slice(at + 1);

    if (!local || !domain) return false;
    if (local.length > 64) return false;
    if (domain.length > 255) return false;

    // domain dot rules
    if (domain.startsWith(".") || domain.endsWith(".")) return false;
    if (domain.includes("..")) return false;

    const lastDot = domain.lastIndexOf(".");
    if (lastDot <= 0 || lastDot === domain.length - 1) return false;

    return true;
  }

  const isEmailValid = useMemo(() => isLikelyEmailNoRegex(email), [email]);

  // keep password policy consistent with helper text
  const isPasswordValid = useMemo(
    () => password.trim().length >= 6,
    [password]
  );

  const isFormValid = isEmailValid && isPasswordValid;

  const submitHandler = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!isFormValid || loading) return;

    setError(null);
    setLoading(true);

    try {
      await signIn({ email, password });
      onLoginSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="signin-dialog-title">
      <DialogTitle id="signin-dialog-title">Login</DialogTitle>

      <Box component="form" onSubmit={submitHandler} noValidate>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1} width={320}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              fullWidth
              error={!!email && !isEmailValid}
              helperText={
                !!email && !isEmailValid
                  ? "Please enter a valid email address."
                  : ""
              }
              autoFocus
              inputProps={{ maxLength: MAX_EMAIL_LEN }}
            />

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              fullWidth
              error={!!password && !isPasswordValid}
              helperText={
                !!password && !isPasswordValid
                  ? "Password must be at least 6 characters."
                  : ""
              }
            />

            {error && (
              <Typography role="alert" color="error" fontSize={14}>
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid || loading}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
